/**
 * tamrielTime.js — Pure functions for ESO/Tamriel time and moon-phase calculation.
 *
 * No DOM access, no side effects. Safe to call at any time.
 */

import {
  TAMRIEL_EPOCH_SEC,
  TAMRIEL_SECS_PER_GAMEDAY,
  TAMRIEL_SECS_PER_GAMEYEAR,
  TAMRIEL_DAYS_IN_MONTH,
  TAMRIEL_MONTHS,
  LUNAR_CYCLE_SEC,
  LUNAR_FULL_EPOCH_SEC,
  LORE_JODE_CYCLE_SEC,
  LORE_JONE_CYCLE_SEC,
  TOD_STATES,
} from '../constants/tamriel.js';

export function getTamrielTime() {
  const nowSec = Date.now() / 1000;
  const offsetTime = nowSec - TAMRIEL_EPOCH_SEC;

  const totalGameDays  = Math.floor(offsetTime / TAMRIEL_SECS_PER_GAMEDAY);
  const gameDayTimeSec = offsetTime % TAMRIEL_SECS_PER_GAMEDAY;

  const secsPerGameHour = TAMRIEL_SECS_PER_GAMEDAY / 24;
  const secsPerGameMin  = secsPerGameHour / 60;
  const gameHour   = Math.floor((gameDayTimeSec / secsPerGameHour) % 24);
  const gameMinute = Math.floor((gameDayTimeSec / secsPerGameMin) % 60);

  const gameYear   = 582 + Math.floor(offsetTime / TAMRIEL_SECS_PER_GAMEYEAR);
  const yearDay    = Math.floor((offsetTime % TAMRIEL_SECS_PER_GAMEYEAR) / TAMRIEL_SECS_PER_GAMEDAY);
  const monthIndex = Math.floor(yearDay / TAMRIEL_DAYS_IN_MONTH) % 12;
  const dayOfMonth = (yearDay % TAMRIEL_DAYS_IN_MONTH) + 1;

  // Moon phase (0 = new, 0.5 = full, 1 = new again). In ESO both moons share
  // the same cycle, so Jode and Jone get the same value. Anchored to a known
  // full moon (LUNAR_FULL_EPOCH_SEC) and offset by half a cycle so phase=0
  // aligns with "new" per the convention above.
  const secsSinceFull = nowSec - LUNAR_FULL_EPOCH_SEC;
  const cycleFrac     = ((secsSinceFull / LUNAR_CYCLE_SEC) % 1 + 1) % 1; // 0=full
  const lunarPhase    = (cycleFrac + 0.5) % 1;                           // 0=new, 0.5=full
  const jonePhase = lunarPhase;
  const jodePhase = lunarPhase;

  // Lore-accurate phases (informational). Anchored to the same full-moon
  // epoch so at a true full moon all four numbers agree.
  const _lorePhase = (cycleSec) => {
    const frac = ((secsSinceFull / cycleSec) % 1 + 1) % 1; // 0=full
    return (frac + 0.5) % 1;                               // 0=new, 0.5=full
  };
  const loreJodePhase = _lorePhase(LORE_JODE_CYCLE_SEC);
  const loreJonePhase = _lorePhase(LORE_JONE_CYCLE_SEC);

  // Time-of-day state
  let todKey = 'midday';
  for (const [key, state] of Object.entries(TOD_STATES)) {
    if (gameHour >= state.hours[0] && gameHour < state.hours[1]) { todKey = key; break; }
  }

  return {
    hour: gameHour,
    minute: gameMinute,
    dayOfMonth,
    monthIndex,
    monthName: TAMRIEL_MONTHS[monthIndex],
    year: gameYear,
    era: '2E',
    jonePhase,
    jodePhase,
    loreJonePhase,
    loreJodePhase,
    todKey,
    todLabel: TOD_STATES[todKey].label,
  };
}

export function getMoonLitStyle(phase, size) {
  // phase: 0=new, 0.5=full, 1=new
  // Convert to illumination percentage (0=dark, 1=fully lit)
  const illum = 1 - Math.abs(phase * 2 - 1);
  const litWidth = Math.round(illum * size);
  // Waxing vs waning determines which side is lit. ESO renders the waxing
  // crescent lit on the LEFT (opposite of the real-world northern-hemisphere
  // convention), so we mirror the in-game art here.
  const isWaxing = phase < 0.5;
  if (isWaxing) {
    return `left:0; width:${litWidth}px;`;
  } else {
    return `right:0; width:${litWidth}px;`;
  }
}
